// Minimal markdown renderer for LLM coaching output. The briefing system
// prompts constrain the model to headings, bold, and bullet/numbered lists,
// so a full markdown library isn't needed. Anything unrecognised renders as
// a plain paragraph, which also keeps partially-streamed lines readable.

// A line that acts as a section heading: "## Turn 4 …" or a standalone
// fully-bold line ("**Turn 4 — 0.20s lost**"), which small models often use
// instead of markdown headings.
const HEADING_RE   = /^(#{1,4})\s+(.*)$/;
const BOLD_LINE_RE = /^\*\*([^*]+)\*\*$/;

// Split coaching output into sections at each heading, so callers can lay
// the sections out side by side. Text before the first heading (an intro
// sentence, or a stream that hasn't reached a heading yet) is its own section.
export function splitMarkdownSections(text) {
  const sections = [];
  let current = [];
  for (const rawLine of (text || '').split('\n')) {
    const line = rawLine.trim();
    if (HEADING_RE.test(line) || BOLD_LINE_RE.test(line)) {
      if (current.join('').trim()) sections.push(current.join('\n'));
      current = [];
    }
    current.push(rawLine);
  }
  if (current.join('').trim()) sections.push(current.join('\n'));
  return sections;
}

// Inline pass: **bold**, `code`, ![alt](url) images, and [text](url) links.
// The image alternative must precede the link alternative so `![alt](url)`
// doesn't fall through and get matched one character late as a link with a
// stray leading "!".
const INLINE_RE = /\*\*(.+?)\*\*|`([^`]+)`|!\[([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)/g;

function renderInline(text) {
  const nodes = [];
  let lastIndex = 0;
  let key = 0;
  let match;
  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const [, bold, code, imgAlt, imgUrl, linkText, linkUrl] = match;
    if (bold !== undefined) nodes.push(<b key={key++}>{bold}</b>);
    else if (code !== undefined) nodes.push(<code key={key++}>{code}</code>);
    else if (imgUrl !== undefined) nodes.push(<img key={key++} className="mm-img" src={imgUrl} alt={imgAlt} />);
    else nodes.push(<a key={key++} href={linkUrl} target="_blank" rel="noopener noreferrer">{linkText}</a>);
    lastIndex = INLINE_RE.lastIndex;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length === 1 && typeof nodes[0] === 'string' ? nodes[0] : nodes;
}

export default function MiniMarkdown({ text, className }) {
  if (!text) return null;

  const blocks = [];
  let list = null;   // { ordered, items: [] } being accumulated

  const flushList = () => {
    if (list) { blocks.push(list); list = null; }
  };

  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) { flushList(); continue; }

    const heading  = line.match(HEADING_RE);
    const boldLine = line.match(BOLD_LINE_RE);
    const bullet   = line.match(/^[-*]\s+(.*)$/);
    const number   = line.match(/^\d+[.)]\s+(.*)$/);

    if (heading || boldLine) {
      flushList();
      blocks.push(heading
        ? { heading: heading[1].length, text: heading[2] }
        : { heading: 3, text: boldLine[1] });
    } else if (bullet || number) {
      const ordered = !!number;
      if (!list || list.ordered !== ordered) { flushList(); list = { ordered, items: [] }; }
      list.items.push((bullet || number)[1]);
    } else {
      flushList();
      blocks.push({ text: line });
    }
  }
  flushList();

  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.items) {
          const items = b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>);
          return b.ordered ? <ol key={i}>{items}</ol> : <ul key={i}>{items}</ul>;
        }
        if (b.heading) {
          // Everything the coach emits is section-level — h2/h3 keep the
          // document outline sane without huge type.
          return b.heading <= 2
            ? <h2 key={i}>{renderInline(b.text)}</h2>
            : <h3 key={i}>{renderInline(b.text)}</h3>;
        }
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </div>
  );
}
